/**
 * Frontend → backend pricing combo map.
 *
 * The frontend uses UX-friendly IDs (e.g., `stickers/holographic-stickers/
 * permanent-holographic-glossy`) that don't exactly match the backend's
 * `category|format|finish` triple in `/app/backend/data/pricing_table.json`.
 * This module translates between them so a single call to
 * `POST /api/pricing/calculate` returns the authoritative Excel-sourced
 * price for any combo the user picks.
 *
 * Structure: `MAP[categoryId][subcategoryId][materialId] = { category, format, finish }`
 *
 * When a combo is NOT in the map (e.g., `customQuote: true` items like
 * Wall Decals or Specialty Niche Products), `resolveBackendCombo` returns
 * `null` and the caller falls back to the legacy local formula or shows
 * a "Contact for quote" CTA.
 *
 * Adding a new material / subcategory:
 *   1. Pick the matching backend triple from `get_pricing_options()`
 *      (all valid combos live in `/app/backend/utils/pricing_engine.py`).
 *   2. Add a new entry under the right category → subcategory → material.
 *   3. Ship — no backend change needed.
 */
const MAP = {
  stickers: {
    'custom-stickers': {
      'white-vinyl-removable-semi': { category: 'Stickers', format: 'Die-Cut', finish: 'White Vinyl Removable Semi-Gloss' },
      'white-vinyl-permanent-semi': { category: 'Stickers', format: 'Die-Cut', finish: 'White Vinyl Permanent Semi-Gloss' },
    },
    'die-cut-singles': {
      'white-vinyl-removable-semi': { category: 'Stickers', format: 'Die-Cut', finish: 'White Vinyl Removable Semi-Gloss' },
      'white-vinyl-permanent-semi': { category: 'Stickers', format: 'Die-Cut', finish: 'White Vinyl Permanent Semi-Gloss' },
    },
    'glitter-stickers': {
      'permanent-glitter-glossy': { category: 'Stickers', format: 'Die-Cut', finish: 'Permanent Glitter Permanent Glossy' },
    },
    'holographic-stickers': {
      'permanent-holographic-glossy': { category: 'Stickers', format: 'Die-Cut', finish: 'Permanent Holographic Permanent Glossy' },
    },
    'permanent-stickers': {
      // Page-based product uses Labels Page (Single Design) White Vinyl
      // Permanent Glossy as the nearest authoritative row.
      'white-vinyl-permanent': { category: 'Labels', format: 'Page (Single Design)', finish: 'White Vinyl Permanent Glossy' },
    },
    'transparent-stickers': {
      'clear-vinyl-removable-semi': { category: 'Stickers', format: 'Die-Cut', finish: 'Clear Vinyl Removable Semi-Gloss' },
    },
  },

  labels: {
    'clear-bopp-rolls': {
      'clear-bopp-permanent-glossy': { category: 'Labels', format: 'Rolls', finish: 'Clear BOPP Permanent Glossy' },
    },
    'white-bopp-rolls': {
      'white-bopp-permanent-glossy-laminated': { category: 'Labels', format: 'Rolls', finish: 'White BOPP Permanent Glossy (Laminated)' },
      'white-paper-permanent-glossy-laminated': { category: 'Labels', format: 'Rolls', finish: 'White Paper Permanent Glossy (Laminated)' },
    },
    'gold-silver-foil': {
      'colored-foil-permanent-shiny': { category: 'Labels', format: 'Rolls', finish: 'Silver Foil Permanent Metallic Silver' },
    },
    'semigloss-paper': {
      'white-paper-permanent-semi-gloss-eco': { category: 'Labels', format: 'Rolls', finish: 'White Paper Permanent Semi-Gloss (Eco-Safe)' },
    },
    'white-vinyl-pages': {
      'white-vinyl-removable-glossy': { category: 'Labels', format: 'Page (Single Design)', finish: 'White Vinyl Removable Glossy' },
      'white-vinyl-permanent-glossy': { category: 'Labels', format: 'Page (Single Design)', finish: 'White Vinyl Permanent Glossy' },
      'white-vinyl-removable-matte': { category: 'Labels', format: 'Page (Single Design)', finish: 'White Vinyl Removable Matte' },
    },
    'writable-bopp': {
      'white-bopp-permanent-matte-writeable': { category: 'Labels', format: 'Rolls', finish: 'White BOPP Permanent Matte (Writeable)' },
    },
  },

  decals: {
    'clear-decals': {
      'clear-decals-crystal-clear-glossy': { category: 'Decals & Clings', format: 'Wall & Window', finish: 'Crystal Clear Vinyl Removable Glossy' },
    },
    'indoor-floor-decals': {
      'indoor-floor-decals-semi-gloss-permanent': { category: 'Decals & Clings', format: 'Floor & Outdoor', finish: 'White Vinyl - Smooth Surface Removable Glossy' },
    },
    'street-decals': {
      // Slip-resistant laminated outdoor decals — price against the same
      // Floor & Outdoor row as indoor-floor decals (nearest xlsx match).
      'street-decals-slip-resistant-laminated': { category: 'Decals & Clings', format: 'Floor & Outdoor', finish: 'White Vinyl - Smooth Surface Removable Glossy' },
    },
    'vinyl-graphics': {
      'vinyl-graphics-white-vinyl-removable': { category: 'Decals & Clings', format: 'Transfers', finish: 'Vinyl Lettering Removable Glossy' },
    },
    'static-clings': {
      'clear-static-clings-kiss-cut': { category: 'Decals & Clings', format: 'Static Clings', finish: 'Clear Static Removable Glossy' },
      'white-static-clings-kiss-cut': { category: 'Decals & Clings', format: 'Static Clings', finish: 'White Static Removable Glossy' },
    },
    'wall-decals': {
      'wall-decals-white-vinyl-removable': { category: 'Decals & Clings', format: 'Wall & Window', finish: 'White Vinyl Removable Matte' },
    },
  },

  'iron-ons': {
    'classic-tattoos': {
      // Temp tattoos (all 3 forms) map to the DTF Heat Transfers row in the
      // backend for now — no dedicated temp-tattoo row exists in the xlsx.
      'temp-tattoos-kiss-cut': { category: 'Wearables', format: 'DTF Heat Transfers', finish: 'Image Transfer Iron-Ons (DTF)' },
      'temp-tattoos-page-multiple': { category: 'Wearables', format: 'DTF Heat Transfers', finish: 'Image Transfer Iron-Ons (DTF)' },
      'temp-tattoos-page-single': { category: 'Wearables', format: 'DTF Heat Transfers', finish: 'Image Transfer Iron-Ons (DTF)' },
    },
    'dtf-heat-transfers': {
      'permanent-image-transfer-dtf': { category: 'Wearables', format: 'DTF Heat Transfers', finish: 'Image Transfer Iron-Ons (DTF)' },
    },
    'dtf-by-sheet': {
      // Same backend row as roll-based DTF — the distinction is fulfillment
      // format only; the per-sq-in price curve is identical.
      'permanent-image-transfer-dtf': { category: 'Wearables', format: 'DTF Heat Transfers', finish: 'Image Transfer Iron-Ons (DTF)' },
    },
    'tattoo-booklets': {
      // No dedicated booklet row in the xlsx — all 3 layouts share the
      // DTF-Heat-Transfers curve (same tattoo substrate + application).
      'temp-tattoos-kiss-cut': { category: 'Wearables', format: 'DTF Heat Transfers', finish: 'Image Transfer Iron-Ons (DTF)' },
      'temp-tattoos-page-multiple': { category: 'Wearables', format: 'DTF Heat Transfers', finish: 'Image Transfer Iron-Ons (DTF)' },
      'temp-tattoos-page-single': { category: 'Wearables', format: 'DTF Heat Transfers', finish: 'Image Transfer Iron-Ons (DTF)' },
    },
    'emblems-patches': {
      'patches-canvas-matte': { category: 'Wearables', format: 'Patches', finish: 'Standard' },
    },
    'name-badges': {
      'name-badges-styrene-matte-magnetic': { category: 'Wearables', format: 'Badges', finish: 'Standard' },
    },
  },

  magnets: {
    'glow-in-dark': {
      'glow-in-dark-die-cut-permanent': { category: 'Stickers', format: 'Die-Cut', finish: 'Glow In The Dark Permanent Stickers' },
    },
    magnets: {
      'magnets-20mil-matte-fridge': { category: 'Unique Custom Products', format: 'Die-Cut Fridge Magnets', finish: 'Standard' },
    },
    'uv-dtf-stickers': {
      'uv-dtf-permanent-stickers': { category: 'Unique Custom Products', format: 'Image Transfers', finish: 'Transfer Decal Permanent Glossy' },
    },
    'vehicle-magnets': {
      'magnets-30mil-matte-vehicle': { category: 'Unique Custom Products', format: 'Die-Cut Car Magnets', finish: 'Standard' },
    },
  },
};

/**
 * Resolve a `(selectedProduct, selectedFormat, selectedMaterial)` triple
 * into the backend pricing combo expected by `/api/pricing/calculate`.
 *
 * @param {string} categoryId - Frontend category id (e.g., 'stickers')
 * @param {string} subcategoryId - Frontend subcategory id (e.g., 'holographic-stickers')
 * @param {string} materialId - Frontend material id (e.g., 'permanent-holographic-glossy')
 * @returns {{ category: string, format: string, finish: string } | null}
 */
export function resolveBackendCombo(categoryId, subcategoryId, materialId) {
  if (!categoryId || !subcategoryId || !materialId) return null;
  return MAP[categoryId]?.[subcategoryId]?.[materialId] ?? null;
}

export default resolveBackendCombo;
